import { useCallback, useRef, useState } from "react";
import YouverifyLiveness from "youverify-liveness-web";

/* ─── Default SDK Configuration ─── */

const DEFAULT_TASKS = [
  { id: "motions" },
];

const DEFAULT_BRANDING = {
  color: "#1A1A31",
  logo: "https://www.visaro.ng/logo-512.png",
};

/* ─── Types ─── */

export type LivenessStatus = "idle" | "running" | "success" | "failed";

export interface UseLivenessCheckOptions {
  /** sessionId generated by the backend via POST /account/user/account/liveness/session */
  sessionId: string;
  /** sessionToken generated by the backend via POST /account/user/account/liveness/session */
  sessionToken: string;
  /** Called when the SDK finishes successfully. Receives the raw SDK payload. */
  onSuccess?: (payload: any) => void;
  /** Called when the SDK reports a failure. */
  onFailure?: (error: any) => void;
  /** Called when the user closes the SDK overlay. */
  onClose?: () => void;
  /** Optional user info passed to the SDK for tracking. */
  user?: { firstName?: string; lastName?: string; email?: string };
  /** Whether to allow audio prompts (default: false). */
  allowAudio?: boolean;
}

/* ─── Hook ─── */

const SDK_TIMEOUT_MS = 15_000; // 15 seconds for SDK to open

const useLivenessCheck = (options: UseLivenessCheckOptions) => {
  const [status, setStatus] = useState<LivenessStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  // Keep a stable reference to the latest options without re-triggering start's deps
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSdkTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    setError(null);
    setStatus("running");
    clearSdkTimeout();

    try {
      const opts = optionsRef.current;

      const config: any = {
        tasks: DEFAULT_TASKS,
        sessionId: opts.sessionId,
        sessionToken: opts.sessionToken,
        branding: DEFAULT_BRANDING,
        allowAudio: opts.allowAudio ?? false,
        sandboxEnvironment: process.env.NODE_ENV !== "production",
        onSuccess: (payload: any) => {
          clearSdkTimeout();
          setStatus("success");
          opts.onSuccess?.(payload);
        },
        onFailure: (err: any) => {
          clearSdkTimeout();
          setStatus("failed");
          setError(
            err?.message || "Liveness verification failed. Please try again."
          );
          opts.onFailure?.(err);
        },
        onClose: () => {
          clearSdkTimeout();
          setStatus((prev) => (prev === "running" ? "idle" : prev));
          opts.onClose?.();
        },
      };

      if (opts.user) {
        config.user = opts.user;
      }

      const yvLiveness = new YouverifyLiveness(config);
      yvLiveness.start();

      // Safety timeout: if SDK never calls back, reset so user can retry
      timeoutRef.current = setTimeout(() => {
        setStatus((prev) => {
          if (prev === "running") {
            setError("Liveness check timed out. Please try again.");
            return "failed";
          }
          return prev;
        });
      }, SDK_TIMEOUT_MS);
    } catch (e: any) {
      clearSdkTimeout();
      setError(e?.message || "Unable to start liveness at this time.");
      setStatus("failed");
    }
  }, [clearSdkTimeout]);

  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
  }, []);

  return { start, status, error, reset };
};

export default useLivenessCheck;
